import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, HelpCircle, AlertCircle } from 'lucide-react';
import { Card } from "@/components/ui/card";

const Slide2Problem = () => {
  return (
    <div className="w-full h-full bg-background flex flex-col lg:flex-row relative overflow-hidden">
      {/* Background Grid Texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
      />
      {/* Left Panel - The Paradox of Access */}
      <div className="w-full lg:w-1/2 h-full p-8 lg:p-16 flex flex-col justify-center border-r border-border/30 relative">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Smartphone className="w-8 h-8 text-primary" />
            </div>
            <span className="text-xl text-primary font-bold tracking-tight">The Paradox of Access</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-foreground mb-8 tracking-tighter leading-[1.1]">
            Everyone has an App,<br />
            <span className="text-muted-foreground italic">But no one has a Plan.</span>
          </h2>

          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-primary font-mono text-xs uppercase tracking-[0.2em] font-black">The Reality</p>
              <ul className="space-y-4 text-lg lg:text-xl text-muted-foreground">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0" />
                  <span>Millions use payment and tracking apps daily.</span>
                </li>
                <li className="flex gap-3">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0" />
                  <span>Yet, 70% of professionals feel "Financial Anxiety."</span>
                </li>
              </ul>
            </div>

            <div className="p-6 bg-primary/5 border border-primary/20 rounded-2xl">
              <p className="text-sm font-bold text-primary mb-2 uppercase tracking-widest">In Layman's Terms:</p>
              <p className="text-muted-foreground italic leading-relaxed">
                "It's like having a high-end GPS in your car, but not knowing where you want to go. You see the roads, but you're still lost."
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - The Direction Gap */}
      <div className="w-full lg:w-1/2 h-full p-8 lg:p-16 flex flex-col justify-center bg-card/30 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-destructive/5 to-transparent pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-destructive/10 rounded-xl">
              <HelpCircle className="w-8 h-8 text-destructive" />
            </div>
            <span className="text-xl text-destructive font-bold tracking-tight">The Direction Gap</span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold text-foreground mb-8 tracking-tighter leading-[1.1]">
            Data alone is just<br />
            <span className="text-destructive">Beautiful Noise.</span>
          </h2>

          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-destructive font-mono text-xs uppercase tracking-[0.2em] font-black">The Problem</p>
              <ul className="space-y-4 text-lg lg:text-xl text-muted-foreground">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-destructive rounded-full mt-2 shrink-0" />
                  <span>Knowing you spent ₹500 is just Information.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-destructive rounded-full mt-2 shrink-0" />
                  <span>Knowing how to save ₹500 for a goal is a Strategy.</span>
                </li>
              </ul>
            </div>

            <Card className="bg-destructive/10 border-destructive/30 p-8 shadow-[0_0_40px_rgba(255,0,0,0.1)]">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-8 h-8 text-destructive shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Choice Paralysis</h3>
                  <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                    Personalized advice is either too expensive (Private Banks) or too generic (Internet Blogs). Users end up doing nothing.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Slide2Problem;